import React, { useMemo, useState } from 'react';
import { Alert, Button, Collapse, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { Route } from '../../../types';
import type { RouteViewModel } from '../../../types/routeViewModel';
import {
  buildMutationTimelineFromAudits,
  buildMutationTimelineFromHydration
} from '../../mutationWorkspace/buildMutationTimelinePanel';
import { MutationTimelinePanel } from '../../mutationWorkspace/MutationTimelinePanel';
import { stopLabel, viewStopToDraft } from '../../mutationWorkspace/mutationStopHelpers';
import { useGetExecutionProgressQuery } from '../../../store/api/routesApi';
import '../../mutationWorkspace/mutationWorkspace.css';
import { useCorrectionContext } from '../hooks/useCorrectionContext';
import { isRouteCorrectionUxV2Enabled } from '../routeCorrectionUxV2Flags';
import { CorrectionWorkspaceShell } from '../shell/CorrectionWorkspaceShell';
import type { CorrectionScenarioId } from '../scenarios/correctionScenarioTypes';
import { CorrectionContextPanel } from './CorrectionContextPanel';
import { ResourceCorrectionPanel } from './ResourceCorrectionPanel';
import { StopCorrectionPanel } from './StopCorrectionPanel';
import { CargoSegmentPanel } from './CargoSegmentPanel';
import { ConsequencePreviewPanel } from './ConsequencePreviewPanel';
import { RouteManagementPanel } from './RouteManagementPanel';
import { PenaltyAdvisoryBanner } from './PenaltyAdvisoryBanner';
import { AddUnloadWizard } from '../wizards/AddUnloadWizard';

type Props = {
  route: Route;
  routeId: number;
  view: RouteViewModel;
  onSuccess: () => void;
};

function CorrectionWorkspaceSharedAlerts({
  ctx,
  showV1Intro
}: {
  ctx: ReturnType<typeof useCorrectionContext>;
  showV1Intro: boolean;
}) {
  return (
    <>
      {showV1Intro ? (
        <Alert
          type="info"
          showIcon
          message="Корректировка маршрута"
          description="Операционная корректировка оставшегося пути. История исполнения сохраняется."
        />
      ) : null}

      <PenaltyAdvisoryBanner advisories={ctx.correction.advisories} />

      {!ctx.fromBackendSlice && (
        <Alert
          type="warning"
          showIcon
          message="Контекст корректировки построен локально — обновите view для серверных band."
          style={{ marginBottom: 8 }}
        />
      )}
    </>
  );
}

function RouteCorrectionWorkspaceV1({ route, routeId, view, onSuccess }: Props) {
  const ctx = useCorrectionContext(routeId, view);
  const [unloadWizardOpen, setUnloadWizardOpen] = useState(false);

  const { data: executionProgress } = useGetExecutionProgressQuery(routeId);

  const stopLabels = useMemo(() => {
    const map = new Map<number, string>();
    (view.structure?.stops ?? []).forEach((s, i) => {
      const draft = viewStopToDraft(s, i);
      if (draft.id != null) map.set(Number(draft.id), stopLabel(draft));
    });
    return map;
  }, [view.structure?.stops]);

  const timelineItems = useMemo(() => {
    const fromHydration = buildMutationTimelineFromHydration(
      executionProgress?.operationalTimeline?.entries
    );
    if (fromHydration.length) return fromHydration;
    return buildMutationTimelineFromAudits(
      executionProgress?.recentOperationalAudits as
        | Array<{ action: string; createdAt: string; context?: unknown; newState?: unknown }>
        | undefined
    );
  }, [executionProgress]);

  const collapseItems = [
    {
      key: 'context',
      label: 'Контекст',
      children: <CorrectionContextPanel ctx={ctx} stopLabels={stopLabels} />
    },
    {
      key: 'resources',
      label: 'Ресурсы',
      children: (
        <ResourceCorrectionPanel routeId={routeId} view={view} ctx={ctx} onSuccess={onSuccess} />
      )
    },
    {
      key: 'stops',
      label: 'Точки маршрута',
      children: (
        <StopCorrectionPanel routeId={routeId} view={view} ctx={ctx} onSuccess={onSuccess} />
      )
    },
    {
      key: 'cargo',
      label: 'Сегмент груза',
      children: <CargoSegmentPanel routeId={routeId} view={view} ctx={ctx} onSuccess={onSuccess} />
    },
    {
      key: 'unload',
      label: 'Доп. выгрузка',
      children: (
        <Space direction="vertical">
          {ctx.isOperationAllowed('ADD_UNLOAD') ? (
            <>
              <Button icon={<PlusOutlined />} onClick={() => setUnloadWizardOpen(true)}>
                Добавить выгрузку
              </Button>
              <AddUnloadWizard
                routeId={routeId}
                view={view}
                ctx={ctx}
                open={unloadWizardOpen}
                onClose={() => setUnloadWizardOpen(false)}
                onSuccess={() => {
                  setUnloadWizardOpen(false);
                  onSuccess();
                }}
              />
            </>
          ) : (
            <Alert type="info" message="Добавление выгрузки недоступно" />
          )}
        </Space>
      )
    },
    {
      key: 'management',
      label: 'Управление',
      children: (
        <RouteManagementPanel
          route={route}
          routeId={routeId}
          view={view}
          ctx={ctx}
          onSuccess={onSuccess}
        />
      )
    },
    {
      key: 'finance',
      label: 'Финансы (просмотр)',
      children: <ConsequencePreviewPanel view={view} />
    }
  ];

  return (
    <div className="mutation-workspace">
      <CorrectionWorkspaceSharedAlerts ctx={ctx} showV1Intro />

      <div className="mutation-workspace__columns">
        <div className="mutation-workspace__main">
          <Collapse defaultActiveKey={['context', 'resources', 'stops']} items={collapseItems} />
        </div>

        <div className="mutation-workspace__sidebar">
          <div className="mutation-workspace__section-title">История изменений</div>
          <MutationTimelinePanel items={timelineItems} />
        </div>
      </div>
    </div>
  );
}

export function RouteCorrectionWorkspaceTab({ route, routeId, view, onSuccess }: Props) {
  const uxV2 = isRouteCorrectionUxV2Enabled();
  const ctx = useCorrectionContext(routeId, view);
  const [selectedScenarioId, setSelectedScenarioId] = useState<CorrectionScenarioId | null>(null);

  if (ctx.correction.isTerminalRoute) {
    return (
      <Alert
        type="info"
        showIcon
        message="Корректировка недоступна"
        description="Маршрут находится в терминальном статусе."
      />
    );
  }

  if (uxV2) {
    return (
      <div className="mutation-workspace">
        <CorrectionWorkspaceSharedAlerts ctx={ctx} showV1Intro={false} />
        <CorrectionWorkspaceShell
          route={route}
          routeId={routeId}
          view={view}
          ctx={ctx}
          selectedScenarioId={selectedScenarioId}
          onSelectScenario={(id) => {
            if (id === 'ADD_CARGO') return;
            setSelectedScenarioId(id);
          }}
          onSuccess={onSuccess}
        />
      </div>
    );
  }

  return <RouteCorrectionWorkspaceV1 route={route} routeId={routeId} view={view} onSuccess={onSuccess} />;
}
